import { toast } from '../../utils/toast';

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

export class ErrorUtils {
  static getApiErrorInfo(error : any) {

    // Check for axios response data first (most specific)
    if (error?.response?.data) {
      const data = error.response.data;
      const status = typeof error.response.status === 'number' ? error.response.status : undefined;

      // Common backend shape: { error: "msg", error_code: "CODE" }
      if (data?.error) {
        if (typeof data.error === 'string') {
          return {
            message: data.error,
            code: data.error_code || data.code,
            status,
            details: data
          } as ApiError;
        }
        // If backend already returns structured error object, normalize into ApiError-ish shape
        if (typeof data.error === 'object') {
          return {
            message: data.error.message || data.error.error || 'Request failed',
            code: data.error.code || data.error.error_code || data.error_code || data.code,
            status,
            details: data.error.details || data
          } as ApiError;
        }
      }

      // Alternate shape: { message, code, ... }
      if (data?.message && typeof data.message === 'string') {
        return {
          message: data.message,
          code: data.error_code || data.code,
          status,
          details: data
        } as ApiError;
      }
    }

    // Check for direct data error
    if (error?.data?.error) {
      if (typeof error.data.error === 'string') {
        return { message: error.data.error, code: error.data.error_code || error.data.code, details: error.data } as ApiError;
      }
      return error.data.error
    }

    // Check for error message (fallback)
    if (error.message) {
      return { status: error.status, message: error.message } as ApiError
    }

    // Check for request without response
    if (error.request) {
      return { status: 400, message: 'Not found' } as ApiError
    }

    // Generic error fallback
    if (error instanceof Error) {
      return { status: 500, message: error.message } as ApiError
    }

    // Final fallback
    return { status: 500, message: 'Something went wrong' } as ApiError
  }
}
export interface ErrorHandlingOptions {
  showToast?: boolean;
  showAlert?: boolean;
  context?: string;
  fallbackMessage?: string;
}

export class ErrorHandler {
  /**
   * Handles API errors in a consistent way across the application
   * @param error - The error object from API response or catch block
   * @param options - Configuration options for error handling
   * @returns Formatted error message
   */
  static handleApiError(
    error: any, 
    options: ErrorHandlingOptions = {}
  ): string {
    const {
      showToast = true,
      showAlert = true,
      context = 'operation',
      fallbackMessage = 'An unexpected error occurred'
    } = options;

    console.error(`${context} error:`, error);
    
    // Extract error message from different response formats
    let errorMessage = fallbackMessage;
    let errorType: 'error' | 'warning' = 'error';
    
    if (error && typeof error === 'object') {
      // Handle API error response format
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error && error.error.message) {
        // Handle nested error structure
        errorMessage = error.error.message;
      } else if (error.details) {
        // Handle validation errors
        errorMessage = Array.isArray(error.details) 
          ? error.details.join(', ')
          : error.details;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Categorize error types for appropriate handling
    const categorizedError = this.categorizeError(errorMessage);
    errorMessage = categorizedError.message;
    errorType = categorizedError.type;

    // Show toast notification if enabled
    if (showToast) {
      this.showToast(errorMessage, errorType);
    }

    return errorMessage;
  }

  /**
   * Categorizes errors based on message content for appropriate handling
   */
  private static categorizeError(message: string): { message: string; type: 'error' | 'warning' } {
    const lowerMessage = message.toLowerCase();
    
    // Duplicate/conflict errors
    if (lowerMessage.includes('already exists') || 
        lowerMessage.includes('duplicate') || 
        lowerMessage.includes('conflict')) {
      return {
        message: `Operation failed: ${message}. Please try with different parameters.`,
        type: 'warning'
      };
    }

    // Validation errors
    if (lowerMessage.includes('validation') || 
        lowerMessage.includes('invalid') || 
        lowerMessage.includes('required') ||
        lowerMessage.includes('format')) {
      return {
        message: `Validation error: ${message}`,
        type: 'warning'
      };
    }

    // Permission/authorization errors
    if (lowerMessage.includes('permission') || 
        lowerMessage.includes('unauthorized') || 
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('access denied')) {
      return {
        message: `Access denied: ${message}`,
        type: 'error'
      };
    }

    // Network/connection errors
    if (lowerMessage.includes('network') || 
        lowerMessage.includes('connection') || 
        lowerMessage.includes('fetch')) {
      return {
        message: 'Network error: Please check your connection and try again.',
        type: 'error'
      };
    }

    // Timeout errors
    if (lowerMessage.includes('timeout') || 
        lowerMessage.includes('timed out')) {
      return {
        message: 'Request timeout: Please try again.',
        type: 'warning'
      };
    }

    // Server errors
    if (lowerMessage.includes('server error') || 
        lowerMessage.includes('internal error') ||
        lowerMessage.includes('500')) {
      return {
        message: 'Server error: Please try again later.',
        type: 'error'
      };
    }

    // Not found errors
    if (lowerMessage.includes('not found') || 
        lowerMessage.includes('404')) {
      return {
        message: `Resource not found: ${message}`,
        type: 'warning'
      };
    }

    // Rate limiting
    if (lowerMessage.includes('rate limit') || 
        lowerMessage.includes('too many requests')) {
      return {
        message: 'Too many requests: Please wait a moment and try again.',
        type: 'warning'
      };
    }

    // Default case
    return {
      message: message,
      type: 'error'
    };
  }

  /**
   * Shows appropriate toast notification based on error type
   */
  private static showToast(message: string, type: 'error' | 'warning'): void {
    switch (type) {
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast.error(message);
        break;
    }
  }

  /**
   * Handles project-specific errors with custom messages
   */
  static handleProjectError(error: any, operation: 'create' | 'update' | 'delete' | 'fetch' = 'create'): string {
    const context = `Project ${operation}`;
    const fallbackMessage = `Project ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles configuration-specific errors
   */
  static handleConfigError(error: any, operation: 'save' | 'load' | 'update' = 'save'): string {
    const context = `Configuration ${operation}`;
    const fallbackMessage = `Configuration ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles environment-specific errors
   */
  static handleEnvironmentError(error: any, operation: 'create' | 'update' | 'delete' = 'create'): string {
    const context = `Environment ${operation}`;
    const fallbackMessage = `Environment ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles workflow-specific errors
   */
  static handleWorkflowError(error: any, operation: 'create' | 'update' | 'delete' | 'execute' = 'create'): string {
    const context = `Workflow ${operation}`;
    const fallbackMessage = `Workflow ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles recommendation-specific errors
   */
  static handleRecommendationError(error: any, operation: 'create' | 'update' | 'delete' | 'upload' = 'create'): string {
    const context = `Recommendation ${operation}`;
    const fallbackMessage = `Recommendation ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }
}

// Export a simple function for backward compatibility
export const handleApiError = (error: any, context?: string): string => {
  return ErrorHandler.handleApiError(error, { context });
};

/**
 * Formats chat response text by parsing JSON and handling escaped characters
 * @param text - The raw response text from the chat API
 * @returns Formatted and readable text
 */
export const formatChatResponse = (text: string): string => {
  try {
    // First, try to parse as JSON to handle the full response structure
    const parsed = JSON.parse(text);
    
    // If it has an answer property, format that
    if (parsed.answer) {
      return formatChatResponse(parsed.answer);
    }
    
    // If it's a string, return as is
    if (typeof parsed === 'string') {
      return parsed;
    }
    
    // If it's an object, try to stringify it nicely
    return JSON.stringify(parsed, null, 2);
  } catch (jsonError) {
    // If JSON parsing fails, try to clean up escaped characters
    try {
      // Handle common escaped characters
      let cleaned = text
        .replace(/\\"/g, '"')  // Replace \" with "
        .replace(/\\n/g, '\n') // Replace \n with actual newlines
        .replace(/\\t/g, '\t') // Replace \t with actual tabs
        .replace(/\\\\/g, '\\'); // Replace \\ with \
      
      // Try to parse again if it looks like JSON
      if (cleaned.trim().startsWith('{') || cleaned.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(cleaned);
          if (parsed.answer) {
            return parsed.answer;
          }
          return JSON.stringify(parsed, null, 2);
        } catch (parseError) {
          // If parsing still fails, return the cleaned text
          return cleaned;
        }
      }
      
      return cleaned;
    } catch (finalError) {
      // If all else fails, return the original text
      return text;
    }
  }
};